import { useState, type ReactNode } from 'react';
import {
  ArrowDownIcon,
  ArrowUpIcon,
  DownloadSimpleIcon,
  PlusIcon,
  TrashIcon,
} from '@phosphor-icons/react';
import { Num, Plural, T, Var, useGT } from 'gt-react';
import type { WorkbookFileOutput as FileOutput } from '@erp/shared-types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { useOutputEditor, type OutputEditor } from './useOutputEditor';
import type { WorkbookEditor } from './useWorkbookEditor';
import { WorkbookExportName } from './WorkbookExportName';
import {
  downloadWorkbookOutput,
  workbookOutputLink,
} from './workbookOutputDownload';
import {
  OutputValidationStatus,
  ValidatedDownloadButton,
} from './WorkbookOutputValidation';
import { outputValidationFailures } from './outputValidationFailures';
import { WorkbookFilePreviewButton } from './WorkbookFilePreview';
import { WorkbookItemIcon } from './workbookItemIcon';
import { WorkbookTable } from './WorkbookTable';
import { outputTypes, resultFilterTarget } from './WorkbookCellMenu';
import { useWorkbookRunResults } from './workbookRunResults';
import { useWorkbookRunTime } from './workbookRunTime';
import {
  addedTab,
  movedTab,
  namedTab,
  type WorkbookOutputState,
} from './workbookOutputs';

/** Previews a File Output as its download would save it, xlsx or (one sheet) CSV. */
export function WorkbookOutputPreviewButton({
  editor,
  output,
  disabled,
  variant,
}: {
  editor: Pick<WorkbookEditor, 'save' | 'workbookId'>;
  output: FileOutput;
  disabled: boolean;
  variant?: 'outline' | 'ghost';
}) {
  return (
    <WorkbookFilePreviewButton
      title={output.fileName}
      formats={output.tabs.length === 1 ? ['xlsx', 'csv'] : ['xlsx']}
      disabled={disabled}
      variant={variant}
      // Viewing a File never distributes it: a preview shows it as it is,
      // even when it failed validation (its status says so).
      load={(format) => workbookOutputLink(editor, output, format, true)}
    />
  );
}

type FileEditor = OutputEditor;

/**
 * A File Output: its xlsx filename, the Worksheets its tabs hold in order (each
 * named after its Worksheet unless renamed), a preview of each tab's Result and
 * the download — xlsx, or CSV while it holds one tab. It downloads only while
 * every tab's Result is Current.
 */
export function WorkbookFileOutput({
  editor,
  output,
  busy,
  act,
  runMenu,
}: {
  editor: FileEditor;
  output: FileOutput;
  busy: boolean;
  act: (operation: () => Promise<unknown>) => Promise<void>;
  /** Runs up to the Worksheets the File holds; absent without Execute. */
  runMenu?: ReactNode;
}) {
  const gt = useGT();
  const { editable, definition, results, state } = useOutputEditor(
    editor,
    output,
    busy,
  );
  const updateFile = (fn: (latest: FileOutput) => FileOutput) =>
    editor.edits
      .output(output.id)
      .update((latest) => (latest.kind === 'file' ? fn(latest) : latest));
  const sheetName = (id: string) =>
    definition.sheets.find((sheet) => sheet.id === id)?.name;
  const unused = definition.sheets.filter(
    (sheet) => !output.tabs.some((tab) => tab.sheetId === sheet.id),
  );
  const failures = outputValidationFailures(definition, output, results);
  const download = (format: 'xlsx' | 'csv', accept: boolean) =>
    void act(() => downloadWorkbookOutput(editor, output, format, accept));
  return (
    <div className="flex min-w-0 flex-1 flex-col gap-3">
      <div
        className="flex flex-wrap items-start gap-2"
        role="group"
        aria-label={gt('File controls', { context: 'workbook file output' })}
      >
        <WorkbookExportName
          key={output.fileName}
          name={output.fileName}
          disabled={!editable}
          change={(fileName) => updateFile((file) => ({ ...file, fileName }))}
        />
        <div className="ml-auto flex items-center gap-1.5">
          {runMenu}
          <WorkbookOutputPreviewButton
            editor={editor}
            output={output}
            disabled={busy || state !== 'current'}
          />
          <ValidatedDownloadButton
            failures={failures}
            disabled={busy || state !== 'current'}
            onDownload={(accept) => download('xlsx', accept)}
          >
            <DownloadSimpleIcon />
            <T context="workbook file output download">Download xlsx</T>
          </ValidatedDownloadButton>
          {output.tabs.length === 1 && (
            <ValidatedDownloadButton
              failures={failures}
              variant="outline"
              disabled={busy || state !== 'current'}
              onDownload={(accept) => download('csv', accept)}
            >
              <DownloadSimpleIcon />
              <T context="workbook file output download">Download CSV</T>
            </ValidatedDownloadButton>
          )}
        </div>
      </div>
      <WorkbookOutputStatus state={state} />
      {state === 'current' && <OutputValidationStatus failures={failures} />}
      <section className="flex flex-col gap-2">
        <h2 className="text-sm font-medium">
          <T context="workbook file output sheets heading">Sheets</T>
        </h2>
        <ol className="flex flex-col gap-1">
          {output.tabs.map((tab, index) => {
            const name = sheetName(tab.sheetId);
            return (
              <li
                key={tab.sheetId}
                className="flex flex-wrap items-center gap-2 rounded border px-2 py-1"
              >
                <WorkbookItemIcon
                  kind="worksheet"
                  className="size-3.5 shrink-0 text-muted-foreground"
                  aria-hidden
                />
                <span className="min-w-32 truncate text-xs">{name}</span>
                <Input
                  key={tab.tabName ?? ''}
                  className="h-7 w-48 text-xs"
                  defaultValue={tab.tabName ?? ''}
                  placeholder={name}
                  maxLength={31}
                  disabled={!editable}
                  aria-label={gt('Sheet name for {sheet}', {
                    context: 'workbook file output sheet name',
                    sheet: name ?? '',
                  })}
                  onBlur={(event) => {
                    const next = namedTab(tab, event.target.value, name);
                    if (next.tabName !== tab.tabName)
                      updateFile((file) => ({
                        ...file,
                        tabs: file.tabs.map((item) =>
                          item.sheetId === tab.sheetId ? next : item,
                        ),
                      }));
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') event.currentTarget.blur();
                  }}
                />
                <div className="ml-auto flex items-center">
                  <TabButton
                    label={gt('Move sheet up', {
                      context: 'workbook file output sheet',
                    })}
                    disabled={!editable || index === 0}
                    onClick={() =>
                      updateFile((file) => ({
                        ...file,
                        tabs: movedTab(file.tabs, tab.sheetId, 'up'),
                      }))
                    }
                  >
                    <ArrowUpIcon />
                  </TabButton>
                  <TabButton
                    label={gt('Move sheet down', {
                      context: 'workbook file output sheet',
                    })}
                    disabled={!editable || index === output.tabs.length - 1}
                    onClick={() =>
                      updateFile((file) => ({
                        ...file,
                        tabs: movedTab(file.tabs, tab.sheetId, 'down'),
                      }))
                    }
                  >
                    <ArrowDownIcon />
                  </TabButton>
                  <TabButton
                    label={gt('Remove sheet', {
                      context: 'workbook file output sheet',
                    })}
                    disabled={!editable}
                    onClick={() =>
                      updateFile((file) => ({
                        ...file,
                        tabs: file.tabs.filter(
                          (item) => item.sheetId !== tab.sheetId,
                        ),
                      }))
                    }
                  >
                    <TrashIcon />
                  </TabButton>
                </div>
              </li>
            );
          })}
        </ol>
        {/* A menu of Worksheets, not a Select: adding one is an action with
            no value to hold, and a Select emptied of its chosen option emits
            a cleared value that became a sheet with no Worksheet. */}
        {editable && unused.length > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                size="sm"
                variant="outline"
                className="h-7 self-start text-xs"
              >
                <PlusIcon />
                <T context="workbook file output add sheet">Add sheet</T>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              {unused.map((sheet) => (
                <DropdownMenuItem
                  key={sheet.id}
                  onClick={() => updateFile((file) => addedTab(file, sheet.id))}
                >
                  <WorkbookItemIcon kind="worksheet" aria-hidden />
                  {sheet.name}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </section>
      <WorkbookFilePreview
        editor={editor}
        output={output}
        filterable={editable}
      />
    </div>
  );
}

function TabButton({
  label,
  disabled,
  onClick,
  children,
}: {
  label: string;
  disabled: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <Button
      size="icon"
      variant="ghost"
      className="size-7"
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={onClick}
    >
      {children}
    </Button>
  );
}

/** What an Output's state means for its download or charts. */
export function WorkbookOutputStatus({
  state,
}: {
  state: WorkbookOutputState;
}) {
  if (state === 'current') return null;
  return (
    <p role="status" className="text-xs text-muted-foreground">
      {state === 'empty' ? (
        <T context="workbook output state">
          Add a worksheet to this output first.
        </T>
      ) : state === 'none' ? (
        <T context="workbook output state">
          Run through this output to produce it.
        </T>
      ) : (
        <T context="workbook output state">
          The workbook changed since the last run. Run through this output to
          include your changes.
        </T>
      )}
    </p>
  );
}

/** Each tab's newest Result, one tab at a time. */
function WorkbookFilePreview({
  editor,
  output,
  filterable,
}: {
  editor: FileEditor;
  output: FileOutput;
  /** The Cell menu may filter the tab's Worksheet. */
  filterable: boolean;
}) {
  const gt = useGT();
  const [chosen, choose] = useState<string | null>(null);
  const results = useWorkbookRunResults(editor);
  const runTime = useWorkbookRunTime(editor.definition);
  const tab =
    output.tabs.find((item) => item.sheetId === chosen) ?? output.tabs[0];
  if (!tab) return null;
  const sheet = editor.definition.sheets.find(
    (item) => item.id === tab.sheetId,
  );
  const { run, result, current } = results.sheet(tab.sheetId);
  return (
    <section className="flex min-w-0 flex-1 flex-col gap-2">
      <div
        className="flex flex-wrap items-center gap-1"
        role="group"
        aria-label={gt('Preview sheet', { context: 'workbook file output' })}
      >
        {output.tabs.map((item) => (
          <Button
            key={item.sheetId}
            size="sm"
            variant="ghost"
            aria-pressed={item.sheetId === tab.sheetId}
            className={cn(
              'h-7 text-xs',
              item.sheetId === tab.sheetId && 'bg-muted',
            )}
            onClick={() => choose(item.sheetId)}
          >
            {item.tabName ??
              editor.definition.sheets.find(
                (entry) => entry.id === item.sheetId,
              )?.name}
          </Button>
        ))}
      </div>
      {result && run ? (
        <>
          <p className="text-xs text-muted-foreground">
            <T context="workbook file output preview count">
              <Num>{result.rowCount}</Num>{' '}
              <Plural n={result.rowCount} one="row" other="rows" /> · Previewing{' '}
              <Num>{result.rows.length}</Num> · Result from{' '}
              <Var>{runTime.short(run.createdAt)}</Var>
            </T>
            {!current && (
              <>
                {' · '}
                <T context="workbook process stale result">
                  Workbook changed since this run
                </T>
              </>
            )}
          </p>
          <WorkbookTable
            fill
            table={result}
            rowNumbers={result.sample?.rowNumbers}
            types={outputTypes(sheet, result.columns)}
            filterTarget={resultFilterTarget(
              sheet,
              filterable && sheet
                ? editor.edits.columnarSheet(sheet.id).update
                : undefined,
            )}
          />
        </>
      ) : (
        <p className="text-xs text-muted-foreground">
          <T context="workbook file output no result">
            No result for this worksheet yet.
          </T>
        </p>
      )}
    </section>
  );
}
